import { PRODUCT_SHIPPING_TYPE } from './../shared/common-code.type';
import { Injectable } from '@angular/core';
import { BaseService, PaginatedResponse, OrderByValue } from '../core';
import {
  CartCreateDto,
  CartIdsDto,
  CartListDto,
  Cart,
  ShippingTariffPrice,
  BuyerCoupon,
  Product,
  ProductListDto,
  CampaignSetting,
  CartCampaignDto,
} from '../dto';
import { Store } from '@ngrx/store';
import * as IndicatorStore from '../store/indicator';
import { mergeMap } from 'rxjs/operators';
import { of } from 'rxjs';

export class PaginatedResponseWithBuyerCouponsAndCampaign<T> extends PaginatedResponse<T> {
  buyerCoupons: BuyerCoupon[];
  campaginSetting: CampaignSetting;
}
@Injectable({
  providedIn: 'root',
})
export class CartService extends BaseService {
  static MIN_ORDER_AMOUNT = 500;
  static MIN_NEXT_ORDER_AMOUNT = 700;

  static ORDER_BY = Object.freeze([
    {
      id: 'Recent-added',
      value: 'orderById',
      key: 'CART.RECENT',
      orderBy: OrderByValue.DESC,
    },
    {
      id: 'Alphabetically',
      value: 'orderByEnglishName',
      key: 'CART.BRAND',
      orderBy: OrderByValue.ASC,
    },
  ]);

  constructor(private store: Store<any>) {
    super();
  }

  getCartItems(cartListDto?: CartListDto) {
    this.store.dispatch(new IndicatorStore.ShowIndicator('getCartItems'));
    return super.get<PaginatedResponseWithBuyerCouponsAndCampaign<Cart>>('/cart', cartListDto).pipe(
      mergeMap((pagenatedCart) => {
        this.store.dispatch(new IndicatorStore.HideIndicator('getCartItems'));
        return of(pagenatedCart);
      })
    );
  }

  getShippingTariffPrice(countryCode: string, cartIdsDto: CartIdsDto) {
    return super.post<ShippingTariffPrice[]>(`/cart/shipping-tariff-price/${countryCode}`, cartIdsDto);
  }

  getSuggestProduct(productListDto?: ProductListDto) {
    return super.get<PaginatedResponse<Product>>('/cart/suggest-product', productListDto);
  }

  addToCart(cartCreateDto: CartCreateDto) {
    this.store.dispatch(new IndicatorStore.ShowIndicator('addToCart'));
    return super.post<Cart>('/cart', cartCreateDto).pipe(
      mergeMap((cart) => {
        this.store.dispatch(new IndicatorStore.HideIndicator('addToCart'));
        return of(cart);
      })
    );
  }

  addToCarts(cartsCreateDto: [CartCreateDto]) {
    this.store.dispatch(new IndicatorStore.ShowIndicator('addToCarts'));
    return super.post<Cart[]>('/cart/bulk', cartsCreateDto).pipe(
      mergeMap((carts) => {
        this.store.dispatch(new IndicatorStore.HideIndicator('addToCarts'));
        return of(carts);
      })
    );
  }

  availableCoupons(cartListDto: CartListDto) {
    this.store.dispatch(new IndicatorStore.ShowIndicator('CartCoupons'));
    return super.post<BuyerCoupon[]>(`/cart/coupon`, cartListDto).pipe(
      mergeMap((buyerCoupon) => {
        this.store.dispatch(new IndicatorStore.HideIndicator('CartCoupons'));
        return of(buyerCoupon);
      })
    );
  }

  downloadCartExcel(cartListDto?: CartListDto) {
    return this.downloadExcel(`/cart/download/excel`, cartListDto.filteredGet());
  }

  updateCartQuantity(id: number, quantity: number, shippingType: PRODUCT_SHIPPING_TYPE) {
    return super.put<Cart>(`/cart/${id}`, { quantity, shippingType });
  }

  removeCartItem(cartIdsDto: CartIdsDto) {
    this.store.dispatch(new IndicatorStore.ShowIndicator('removeCartItem'));
    return super.delete('/cart', cartIdsDto).pipe(
      mergeMap((cartItem) => {
        this.store.dispatch(new IndicatorStore.HideIndicator('removeCartItem'));
        return of(cartItem);
      })
    );
  }

  sampleProductShippingFee(countryCode: string, cartCampaignDto: CartCampaignDto) {
    return super.post<{ itemPrice: number; shippingFee: number; sampleProductShippingFee: number }>(
      `/cart/campaign/sample-product/${countryCode}/price`,
      cartCampaignDto
    );
  }

  campaignSampleProduct(countryCode: string, cartIdsDto: CartIdsDto) {
    this.store.dispatch(new IndicatorStore.ShowIndicator('campaignSampleProduct'));
    return super.post<CampaignSetting[]>(`/cart/campaign/sample-product/${countryCode}`, cartIdsDto).pipe(
      mergeMap((campaignSetting) => {
        this.store.dispatch(new IndicatorStore.HideIndicator('campaignSampleProduct'));
        return of(campaignSetting);
      })
    );
  }
}
